import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { Config, Layout, PlotData } from "plotly.js";
import { ProgressSpinner } from "primereact/progressspinner";
import Plot from "react-plotly.js";
import { mapCumulativeDefectToPlotData } from "../../utils/DefectInsightsGraphHelper";
import { DefectInsightFilterBy } from "../../types/DefectInsightsData";
import { useAppSelector } from "../../hooks/storeHooks";
import { selectDefectInsights } from "../../store/selectors/defectInsightsSelectors";

type CumulativeDefectGraphProps = {
    filterBy: DefectInsightFilterBy;
    filterValues: string[] | undefined;
};

export const CumulativeDefectsGraph = (props: CumulativeDefectGraphProps) => {
    const { filterBy, filterValues } = props;

    const { t } = useTranslation("locale");

    const { filteredCumulativeDefectData, cumulativeDefectLoading } = useAppSelector(selectDefectInsights);

    const [data, setData] = useState<Partial<PlotData>[]>([]);

    useEffect(() => {
        if (!cumulativeDefectLoading) {
            setData(mapCumulativeDefectToPlotData(filteredCumulativeDefectData, filterBy, filterValues));
        }
    }, [filteredCumulativeDefectData, filterValues, cumulativeDefectLoading]);

    const layout = useMemo(() => {
        const graphLayout: Partial<Layout> = {
            autosize: true,
            xaxis: {
                showgrid: false,
                zerolinecolor: "#B1E1E4",
                tickfont: {
                    color: "#555",
                    size: 13,
                },
                tickformat: "%b %y",
                title: t("defectInsights.date"),
                titlefont: {
                    color: "#555",
                    size: 13,
                }
            },
            yaxis: {
                showgrid: true,
                automargin: true,
                categoryarray: ["All"],
                categoryorder: "array",
                rangemode: "tozero",
                tickmode: "array",
                gridcolor: "#B1E1E4",
                zerolinecolor: "#B1E1E4",
                tickfont: {
                    color: '#777',
                    size: 13,
                },
                title: t("defectInsights.numberOfDefects"),
                titlefont: {
                    color: "#555",
                    size: 13,
                }
            },
            margin: {
                pad: 10,
                l: 60,
                t: 20,
                b: 60,
                r: 0,
            },
            font: {
                family: "Open Sans",
                color: "#555",
                size: 12,
            },
            showlegend: true,
            legend: {
                title: {
                    font: {
                        color: "#555",
                        size: 13,
                    },
                    text: t(`defectInsights.defects`)
                },
            },
            plot_bgcolor: "#F4F8FB",
            hovermode: "closest",
            hoverlabel: { 
                bgcolor: '#1C1940',
                bordercolor: '#1C1940',
                font: {
                    color: '#FFF',
                    size: 13,
                 }
            }
        }
        return graphLayout;
    }, [data]);

    const config = useMemo(() => {
        const graphConfig: Partial<Config> = {
            displayModeBar: true,
            responsive: true,
            displaylogo: false,
            scrollZoom: false,
            showTips: false,
            modeBarButtonsToRemove: ['pan2d','zoom2d','autoScale2d','lasso2d','select2d']
        }
        return graphConfig;
    }, []);

    if (cumulativeDefectLoading) {
        return (
            <div className="flex h-screen justify-items-center align-items-center">
                <ProgressSpinner />
            </div>
        )
    }

    if (!data?.length) {
        return (
            <div>
                <h2 className="mb-4">
                    {t("defectInsights.cumulativeDefects")}
                </h2>
                <div className="flex justify-content-center">
                    <p>{t("defectsGraph.noDefectsDataAvailable")}</p>
                </div>
            </div>
        )
    }

    return (
        <div>
            <h2>
                {t("defectInsights.cumulativeDefects")}
            </h2>
            <Plot
                useResizeHandler={true}
                data={data}
                layout={layout}
                config={config}
                className="defect-insight-graph"
            />
        </div>
    )
}
